import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";

const emptyForm = { title: "", date: "", description: "", image_url: "", active: true, order: 0 };

export default function StaffEvents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [draft, setDraft] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/staff/events");
      setItems(data);
      const d = {};
      data.forEach((e) => { d[e.id] = { ...e }; });
      setDraft(d);
    } catch { toast.error("Could not load events."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff/events", { ...form, order: Number(form.order) });
      toast.success("Event added");
      setForm(emptyForm);
      setCreating(false);
      load();
    } catch { toast.error("Create failed"); }
  };

  const update = (id, k, v) => setDraft((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const save = async (id) => {
    try {
      const d = draft[id];
      const { data } = await api.patch(`/staff/events/${id}`, {
        title: d.title, date: d.date, description: d.description, image_url: d.image_url, order: Number(d.order),
      });
      setItems((prev) => prev.map((e) => (e.id === id ? data : e)));
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const toggle = async (ev) => {
    try {
      const { data } = await api.patch(`/staff/events/${ev.id}`, { active: !ev.active });
      setItems((prev) => prev.map((e) => (e.id === ev.id ? data : e)));
      toast.success(data.active ? "Now visible on site" : "Hidden");
    } catch { toast.error("Toggle failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/staff/events/${id}`);
      setItems((prev) => prev.filter((e) => e.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="staff-events">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Events</div>
          <h1 className="mt-2 text-4xl">What we're hosting</h1>
        </div>
        <button data-testid="events-new-btn" onClick={() => setCreating((v) => !v)} className="btn-brass !py-2.5 !px-5 text-sm">
          <Plus size={14} /> {creating ? "Cancel" : "New event"}
        </button>
      </div>

      {creating && (
        <form data-testid="events-new-form" onSubmit={create} className="mt-6 border border-[hsl(var(--brass))] bg-[hsl(var(--bg-2))] p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <input required placeholder="Title" data-testid="new-event-title" className="tbb-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input required placeholder="Date (e.g., 'Every Friday' or '2026-04-12')" data-testid="new-event-date" className="tbb-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <input placeholder="Image URL or path" data-testid="new-event-image" className="tbb-input md:col-span-2" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
          <div className="md:col-span-2 -mt-2">
            <ImageUploader testid="new-event-upload" label="Upload image" onUploaded={(url) => setForm({ ...form, image_url: url })} />
          </div>
          <textarea required rows={3} placeholder="Description" data-testid="new-event-desc" className="tbb-input md:col-span-2 resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <input type="number" placeholder="Order" className="tbb-input" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
          <div className="md:col-span-2">
            <button type="submit" data-testid="new-event-submit" className="btn-brass">Add event</button>
          </div>
        </form>
      )}

      {loading && <div className="mt-8 text-[hsl(var(--ink-soft))]">Loading...</div>}

      <div className="mt-8 space-y-4">
        {items.map((ev) => {
          const d = draft[ev.id] || {};
          return (
            <div key={ev.id} data-testid={`event-row-${ev.id}`} className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4 grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-4 items-start">
              {ev.image_url ? (
                <img src={ev.image_url} alt={ev.title} className="w-full h-24 object-cover" />
              ) : (
                <div className="w-full h-24 bg-[hsl(var(--rule))]" />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                <input className="tbb-input sm:col-span-3" value={d.title || ""} onChange={(e) => update(ev.id, "title", e.target.value)} />
                <input className="tbb-input sm:col-span-2" value={d.date || ""} onChange={(e) => update(ev.id, "date", e.target.value)} />
                <input type="number" className="tbb-input" value={d.order ?? 0} onChange={(e) => update(ev.id, "order", e.target.value)} />
                <input className="tbb-input sm:col-span-6 font-mono text-xs" value={d.image_url || ""} onChange={(e) => update(ev.id, "image_url", e.target.value)} placeholder="Image URL" />
                <div className="sm:col-span-6 -mt-1">
                  <ImageUploader testid={`event-upload-${ev.id}`} label="Upload new image" onUploaded={(url) => update(ev.id, "image_url", url)} />
                </div>
                <textarea rows={2} className="tbb-input sm:col-span-6 resize-none" value={d.description || ""} onChange={(e) => update(ev.id, "description", e.target.value)} />
              </div>
              <div className="flex md:flex-col gap-2">
                <button data-testid={`event-save-${ev.id}`} onClick={() => save(ev.id)} className="inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]">
                  <Save size={12} /> Save
                </button>
                <button data-testid={`event-toggle-${ev.id}`} onClick={() => toggle(ev)} className="inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--rule))]">
                  {ev.active ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
                </button>
                <button data-testid={`event-delete-${ev.id}`} onClick={() => remove(ev.id)} className="inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .tbb-input {
          width: 100%;
          background: hsl(var(--bg));
          border: 1px solid hsl(var(--rule));
          border-radius: 4px;
          padding: 0.6rem 0.85rem;
          font-family: inherit;
          font-size: 0.9rem;
          color: hsl(var(--ink));
        }
        .tbb-input:focus { outline: none; border-color: hsl(var(--forest)); }
      `}</style>
    </div>
  );
}
